import { useState } from 'react';
import { Trans } from 'react-i18next';
import { Formik } from 'formik';
import { logEvent } from '@amplitude/analytics-browser';
import * as Yup from 'yup';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Section from '../../../componentsv2/Section';
import Typography from '../../../componentsv2/Typography';
import Button from '../../../componentsv2/Button';
import InfoDrawer from '../../../componentsv2/InfoDrawer';
import Form from '../../../componentsv2/Form';
import { MultiSelectField, TextField } from '../../../componentsv2/Field';
import NumericFormatField from '../../../componentsv2/Field/NumericFormatField';
import CountryFlag from '../../../componentsv2/CountryFlag';
import MoreMenu from '../../../componentsv2/MoreMenu';
import Skeleton from '../../../componentsv2/Skeleton';
import { AddIcon } from '../../../components/Icons';
import { useSnackbar } from '../../../context/snackbar';
import { useAppSelector } from '../../../store';
import { useTranslation } from '../../../hooks/i18n';
import { useUserLocation } from '../../../hooks/useUserLocation';
import {
  useTransferConfigs,
  useCreateTransferConfig,
  useUpdateTransferConfig,
  useDeleteTransferConfig,
} from '../../../hooks/useTransferConfig';
import { formatCreditLineCurrency } from '../../../utils/helpers';
import { bankingEvents } from '../../../constants/events';
import { TransferConfig } from '../../../types/banking';
import { UserRole } from '../../../types/companyUser';
import { COLORS } from '../../../themev2';

export default function TransferApprovals({ companyId }: { companyId: number }) {
  const { t } = useTranslation(['myAccount']);
  const { language } = useUserLocation();
  const { showSnackbar } = useSnackbar();
  const { companyUser } = useAppSelector((state) => state.companyUser);
  const [showWorkflowDrawer, setShowWorkflowDrawer] = useState(false);
  const [editWorkflow, setEditWorkflow] = useState<TransferConfig | null>(null);

  const users = companyUser?.selectedCompany?.companyUserRoles;

  const { data: transferConfigs, isLoading: isLoadingTransferConfigs } = useTransferConfigs(companyId);
  const { mutate: createTransferConfig, isLoading: isLoadingCreateTransferConfig } =
    useCreateTransferConfig(companyId);
  const { mutate: updateTransferConfig, isLoading: isLoadingUpdateTransferConfig } =
    useUpdateTransferConfig(companyId);
  const { mutate: deleteTransferConfig, isLoading: isLoadingDeleteTransferConfig } =
    useDeleteTransferConfig(companyId);

  const isEmptyTransferConfigs = transferConfigs?.length === 0;
  const isOwner = companyUser?.selectedCompanyRole === UserRole.OWNER;

  return (
    <Section>
      <Grid container alignItems="center">
        <Grid item xs={8}>
          <Typography
            sx={{ fontSize: '18px', color: COLORS.gray[1400], lineHeight: '27px', fontWeight: 600 }}
          >
            {t('transferApprovals.noWorkflowsTitle')}
          </Typography>
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            startIcon={<AddIcon />}
            color="secondary"
            onClick={() => {
              setShowWorkflowDrawer(true);
              logEvent(bankingEvents.CLICK_ADD_TRANSFER_APPROVAL_WORKFLOW);
            }}
            disabled={!isEmptyTransferConfigs || !isOwner}
          >
            {t('transferApprovals.addWorkflowButton')}
          </Button>
        </Grid>
        {isLoadingTransferConfigs && (
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={172} />
          </Grid>
        )}
        {!isLoadingTransferConfigs && isEmptyTransferConfigs ? (
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                minHeight: '172px',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{ color: COLORS.gray[1100], fontSize: '18px', fontWeight: '600', lineHeight: '27px' }}
              >
                {t('transferApprovals.noWorkflowsMessage')}
              </Typography>
              <Typography
                sx={{ color: COLORS.gray[1100], fontSize: '16px', fontWeight: '400', lineHeight: '24px' }}
              >
                {t('transferApprovals.noWorkflowsDescription')}
              </Typography>
            </Box>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Grid container rowSpacing={2}>
              {transferConfigs?.map((config) => (
                <Grid item xs={12} key={config.id}>
                  <Box
                    sx={{
                      backgroundColor: COLORS.gray[200],
                      padding: '16px',
                      borderRadius: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: COLORS.gray[1100],
                        fontSize: '16px',
                        fontWeight: 400,
                        lineHeight: '24px',
                      }}
                    >
                      <Trans
                        i18nKey="transferApprovals.amountRequiresApproval"
                        values={{
                          amount: formatCreditLineCurrency(language, config.maxAmount, 'USD', 2),
                        }}
                        components={[<span style={{ fontWeight: 600 }} />]}
                      >
                        {'<0>{{ amount }} or more</0> requires approval from one of these users'}
                      </Trans>
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {config.users.map((user) => (
                        <Box
                          key={user.id}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#E6F6FB',
                            borderRadius: '16px',
                            padding: '4px 12px',
                            margin: '4px',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: 500,
                              lineHeight: '21px',
                              color: COLORS.gray[1200],
                            }}
                          >
                            {user.name}
                          </Typography>
                        </Box>
                      ))}
                      <MoreMenu
                        disabled={!isOwner}
                        items={[
                          {
                            label: t('transferApprovals.editWorkflow'),
                            onClick: () => {
                              setEditWorkflow(config);
                              setShowWorkflowDrawer(true);
                              logEvent(bankingEvents.CLICK_EDIT_TRANSFER_APPROVAL_WORKFLOW, {
                                'Max Amount': config.maxAmount,
                                Users: config.users.map((user) => user.name).join(', '),
                              });
                            },
                          },
                          {
                            label: t('transferApprovals.deleteWorkflow'),
                            onClick: () => {
                              deleteTransferConfig(config.id);
                              logEvent(bankingEvents.DELETE_TRANSFER_APPROVAL, {
                                'Max Amount': config.maxAmount,
                                Users: config.users.map((user) => user.name).join(', '),
                              });
                            },
                            color: '#C54646',
                            isLoading: isLoadingDeleteTransferConfig,
                          },
                        ]}
                      />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
        <Formik
          initialValues={{
            maxAmount: editWorkflow?.maxAmount || 0,
            users: editWorkflow?.users.map((user) => user.id.toString()) || [],
          }}
          enableReinitialize
          validationSchema={Yup.object().shape({
            maxAmount: Yup.number().min(1, t('common:requiredField')).required(t('common:requiredField')),
            users: Yup.array()
              .of(Yup.string())
              .min(1, t('common:requiredField'))
              .required(t('common:requiredField')),
          })}
          onSubmit={(values) => {
            // map user ids to numbers
            const mappedUsers = values.users.map((user) => Number(user));
            if (editWorkflow) {
              logEvent(bankingEvents.SUBMIT_EDIT_TRANSFER_APPROVAL, {
                'Max Amount': values.maxAmount,
                Users: values.users.join(', '),
              });
              updateTransferConfig(
                {
                  transferConfigId: editWorkflow.id,
                  transferConfigData: { ...values, users: mappedUsers },
                },
                {
                  onSuccess: () => {
                    showSnackbar(t('transferApprovals.successUpdateMessage'), 'success');
                    setShowWorkflowDrawer(false);
                    logEvent(bankingEvents.UPDATED_TRANSFER_APPROVAL, {
                      'Max Amount': values.maxAmount,
                      Users: values.users.join(', '),
                    });
                  },
                  onError: (error: unknown) => {
                    showSnackbar(t('transferApprovals.errorUpdateMessage'), 'error');
                  },
                }
              );
            } else {
              logEvent(bankingEvents.SUBMIT_CREATE_TRANSFER_APPROVAL, {
                'Max Amount': values.maxAmount,
                Users: values.users.join(', '),
              });
              createTransferConfig(
                { ...values, users: mappedUsers },
                {
                  onSuccess: () => {
                    showSnackbar(t('transferApprovals.successMessage'), 'success');
                    setShowWorkflowDrawer(false);
                    logEvent(bankingEvents.CREATED_TRANSFER_APPROVAL, {
                      'Max Amount': values.maxAmount,
                      Users: values.users.join(', '),
                    });
                  },
                  onError: (error: unknown) => {
                    showSnackbar(t('transferApprovals.errorMessage'), 'error');
                  },
                }
              );
            }
          }}
        >
          {({ submitForm, values, setFieldValue }) => {
            return (
              <InfoDrawer
                showDrawer={showWorkflowDrawer}
                submit={submitForm}
                isSubmitting={isLoadingCreateTransferConfig || isLoadingUpdateTransferConfig}
                handleClose={() => {
                  setShowWorkflowDrawer(false);
                }}
                buttonLabel={t('transferApprovals.saveButton')}
              >
                <Form trackingEvent="BANKING_TRANSFER_CONFIG_CREATE_WORKFLOW">
                  <Grid container rowSpacing={2}>
                    <Grid item xs={12} sx={{ marginBottom: '16px' }}>
                      <Typography
                        sx={{
                          fontSize: '24px',
                          fontWeight: 600,
                          color: COLORS.gray[1200],
                        }}
                      >
                        {t('transferApprovals.approvalWorkflowTitle')}
                      </Typography>
                      <Divider sx={{ backgroundColor: COLORS.gray[900], margin: '16px 0' }} />
                      <Typography>{t('transferApprovals.approvalWorkflowDescription')}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={0}>
                        <Grid item xs={12}>
                          <Typography
                            sx={{
                              color: COLORS.gray[1200],
                              fontSize: '14px',
                              fontWeight: 400,
                              lineHeight: '21px',
                            }}
                          >
                            {t('transferApprovals.maxAmountLabel')}
                          </Typography>
                        </Grid>
                        <Grid item xs={5} sm={4} md={3} lg={3}>
                          <TextField
                            readOnly
                            name="currency"
                            value="USD"
                            sx={{
                              '& fieldset': {
                                borderRadius: '8px 0 0 8px', // Only left corners
                              },
                            }}
                            startAdornment={
                              <CountryFlag countryCode="US" size={24} style={{ marginRight: '8px' }} />
                            }
                          />
                        </Grid>
                        <Grid item xs={7} sm={8} md={9} lg={9}>
                          <NumericFormatField
                            name="maxAmount"
                            value={values.maxAmount || ''}
                            hideLabel
                            fullWidth
                            onChange={(value) => {
                              setFieldValue('maxAmount', value.floatValue);
                            }}
                            sx={{
                              '& fieldset': {
                                borderRadius: '0 8px 8px 0',
                              },
                            }}
                            placeholder="$ 10,000"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <MultiSelectField
                        label={t('transferApprovals.usersLabel')}
                        name="users"
                        options={
                          users?.map((user) => ({
                            label: user.user.user.fullName,
                            value: user.user.id.toString(),
                          })) || []
                        }
                        placeholder={t('transferApprovals.usersPlaceholder')}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </InfoDrawer>
            );
          }}
        </Formik>
      </Grid>
    </Section>
  );
}
